/**
 * The shapes this screen reads and writes. Deliberately LOCAL types rather
 * than imports from `lib/income/schema` or `components/categories`: the
 * category backend is being wired by a parallel agent and the entry screen
 * must compile — and degrade — against whatever it returns today.
 */

/** Wallet's first decision, and therefore this screen's. */
export type EntryType = "expense" | "income" | "transfer";

export type CategoryKind = "INCOME" | "EXPENSE";

/** `Expense.paymentMethod`, mirroring the server enum exactly. */
export const PAYMENT_METHODS = [
  { value: "UPI", label: "UPI" },
  { value: "CASH", label: "Cash" },
  { value: "CREDIT_CARD", label: "Credit card" },
  { value: "DEBIT_CARD", label: "Debit card" },
  { value: "BANK_TRANSFER", label: "Bank transfer" },
  { value: "OTHER", label: "Other" },
] as const;

export type PaymentMethod = (typeof PAYMENT_METHODS)[number]["value"];

/**
 * One row of `GET /categories`.
 *
 * `parentId` is what makes the picker two-level (`Snacks` under
 * `Food & Drinks`). It is typed nullable and the picker treats "every row has
 * a null parent" as the FLAT case rather than an error, which is what the API
 * returns until the parallel agent's hierarchy lands.
 */
export interface EntryCategory {
  id: string;
  name: string;
  kind: CategoryKind;
  parentId: string | null;
  color: string | null;
  sortOrder: number;
  isArchived: boolean;
}

/** One row of `GET /accounts`. `color` is the per-person hex the seed sets. */
export interface EntryAccount {
  id: string;
  bankName: string;
  nickname: string | null;
  accountType: string | null;
  color: string | null;
  isArchived: boolean;
}

export interface EntryLabel {
  id: string;
  name: string;
  color: string | null;
}

/**
 * An existing record being edited or duplicated.
 *
 * `externalSourceId` is READ (a rent-synced row is worth a badge) and NEVER
 * written back — see `draft.ts`, which is where that rule is enforced.
 */
export interface EntryRecord {
  id: string;
  type: EntryType;
  date: string;
  amountPaise: number;
  categoryId?: string | null;
  description?: string | null;
  accountId?: string | null;
  fromAccountId?: string | null;
  toAccountId?: string | null;
  paymentMethod?: string | null;
  labelIds?: string[];
  externalSourceId?: string | null;
}

/** What `onSaved` hands back — enough to render a confirmation or duplicate. */
export interface SavedEntry {
  id: string;
  type: EntryType;
  amountPaise: number;
  /** True when the user asked for another entry, so the host must stay open. */
  keepOpen: boolean;
}

export function accountLabel(account: EntryAccount): string {
  return account.nickname?.trim() || account.bankName;
}

/** Neutral slate when an account predates the colour column. */
export const FALLBACK_ACCOUNT_COLOR = "#64748b";

export function accountColor(account: EntryAccount | null | undefined): string {
  return account?.color?.trim() || FALLBACK_ACCOUNT_COLOR;
}
